import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';

// Type definitions
type Intent = 
  | "urgent_request"
  | "emotional_support"
  | "practical_need"
  | "casual_message"
  | "manipulative_language";

type Tone = 
  | "neutral"
  | "stressed"
  | "anxious"
  | "demanding"
  | "affectionate"
  | "vulnerable";

type Category = "supportive" | "neutral" | "clarifying" | "boundary" | "affectionate" | "scheduling";

type Suggestion = { 
  category: Category; 
  text: string;
  reason: string;
};

type AnalysisResult = {
  intent: Intent;
  tone: Tone;
  contextNotes: string[];
  redFlags: string[];
  suggestions: Suggestion[];
  healthScore: number;
};

export default function Analyzer() {
  const [message, setMessage] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [showReplies, setShowReplies] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAnalyze = async () => {
    if (!message.trim()) return;
    
    setIsAnalyzing(true);
    setError(null);
    
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      
      if (!supabaseUrl) {
        throw new Error('Supabase configuration missing');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/analyze-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: message.trim() }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to analyze message');
      }

      const result: AnalysisResult = await response.json();
      setAnalysis(result);
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err instanceof Error ? err.message : 'Failed to analyze message. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleViewReplies = () => {
    setShowReplies(true);
  };

  const getIntentLabel = (intent: Intent): string => {
    const labels: Record<Intent, string> = {
      urgent_request: "Urgent Request",
      emotional_support: "Emotional Support",
      practical_need: "Practical Need",
      casual_message: "Casual Message",
      manipulative_language: "Manipulative Language"
    };
    return labels[intent];
  };

  const getToneLabel = (tone: Tone): string => {
    const labels: Record<Tone, string> = {
      neutral: "Neutral",
      stressed: "Stressed",
      anxious: "Anxious",
      demanding: "Demanding",
      affectionate: "Affectionate",
      vulnerable: "Vulnerable"
    };
    return labels[tone];
  };

  const getCategoryLabel = (category: Category): string => {
    const labels: Record<Category, string> = {
      supportive: "Supportive",
      clarifying: "Clarifying",
      neutral: "Neutral",
      affectionate: "Affectionate",
      scheduling: "Time Management",
      boundary: "Boundary"
    };
    return labels[category];
  };

  const getCategoryColor = (category: Category): { bg: string; text: string } => {
    const colors: Record<Category, { bg: string; text: string }> = {
      supportive: { bg: 'bg-green-100', text: 'text-green-600' },
      clarifying: { bg: 'bg-blue-100', text: 'text-blue-600' },
      neutral: { bg: 'bg-gray-100', text: 'text-gray-600' },
      affectionate: { bg: 'bg-pink-100', text: 'text-pink-600' },
      scheduling: { bg: 'bg-purple-100', text: 'text-purple-600' },
      boundary: { bg: 'bg-red-100', text: 'text-red-600' }
    };
    return colors[category];
  };

  const getCategoryIcon = (category: Category): string => {
    const icons: Record<Category, string> = {
      supportive: "ri-heart-line",
      clarifying: "ri-question-line",
      neutral: "ri-chat-3-line",
      affectionate: "ri-emotion-happy-line",
      scheduling: "ri-calendar-line",
      boundary: "ri-shield-line"
    };
    return icons[category];
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Navigation />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Message Analyzer</h1>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Paste a message you received and get instant insights about tone, intent, and how to respond naturally.
            </p>
            <div className="flex items-center justify-center space-x-2 mt-4">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-gray-600 font-medium">Powered by ChatGPT</span>
            </div>
          </div>

          {/* Input Section */}
          <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 mb-8">
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Paste the message you received
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Example: Hey, can you help me with homework tonight? I'm really stressed about this test..."
              className="w-full h-40 px-4 py-3 border-2 border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 resize-none"
              maxLength={500}
            />
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-gray-500">{message.length}/500 characters</span>
              <button
                onClick={handleAnalyze}
                disabled={!message.trim() || isAnalyzing}
                className="px-6 py-3 bg-blue-600 text-white text-sm font-semibold rounded-full hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isAnalyzing ? (
                  <span className="flex items-center space-x-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    <span>Analyzing...</span>
                  </span>
                ) : (
                  'Analyze Message'
                )}
              </button>
            </div>
            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>

          {/* Analysis Results */}
          {analysis && !showReplies && (
            <div className="space-y-6">
              {/* Health Score */}
              <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Communication Health</h3>
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl font-bold text-gray-900">{analysis.healthScore}</span>
                    <span className="text-sm text-gray-500">/100</span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all duration-1000 ${
                      analysis.healthScore >= 80 ? 'bg-green-500' :
                      analysis.healthScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${analysis.healthScore}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 mt-3">
                  {analysis.healthScore >= 80 ? 'This message shows healthy communication.' :
                   analysis.healthScore >= 60 ? 'This message has some concerns but isn\'t alarming.' :
                   'This message shows unhealthy patterns that need attention.'}
                </p>
              </div>

              {/* Intent & Tone */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                      <i className="ri-compass-line text-xl text-blue-600"></i>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">Intent</h3>
                  </div>
                  <p className="text-sm text-gray-700">{getIntentLabel(analysis.intent)}</p>
                </div>

                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                      <i className="ri-emotion-line text-xl text-purple-600"></i>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">Tone</h3>
                  </div>
                  <p className="text-sm text-gray-700">{getToneLabel(analysis.tone)}</p>
                </div>
              </div>

              {/* Context Notes */}
              {analysis.contextNotes.length > 0 && (
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 md:p-8">
                  <div className="flex items-start space-x-3 mb-4">
                    <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-lightbulb-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 mb-3">Context to Consider</h3>
                      <ul className="space-y-2">
                        {analysis.contextNotes.map((note, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <i className="ri-arrow-right-s-line text-lg text-blue-600 mt-0.5 flex-shrink-0"></i>
                            <span className="text-sm text-gray-700">{note}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Red Flags */}
              {analysis.redFlags.length > 0 && (
                <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 md:p-8">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center">
                      <i className="ri-flag-line text-xl text-white"></i>
                    </div>
                    <h3 className="text-base font-semibold text-red-900">Unhealthy Patterns Detected</h3>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {analysis.redFlags.map((flag, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <i className="ri-close-circle-line text-lg text-red-600 mt-0.5 flex-shrink-0"></i>
                        <span className="text-sm text-red-900">{flag}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="bg-white rounded-xl p-4">
                    <p className="text-sm text-gray-700 mb-3">
                      <strong>You deserve respect.</strong> If you're feeling uncomfortable or pressured, it's okay to set boundaries or talk to a trusted adult.
                    </p>
                    <Link to="/disclaimer" className="text-sm text-red-600 font-medium hover:text-red-700 whitespace-nowrap cursor-pointer">
                      Get Help Resources <i className="ri-arrow-right-line ml-1"></i>
                    </Link>
                  </div>
                </div>
              )}

              {/* Action Button */}
              <div className="flex justify-center">
                <button
                  onClick={handleViewReplies}
                  className="px-8 py-4 bg-blue-600 text-white text-base font-semibold rounded-full hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  See Reply Suggestions
                </button>
              </div>
            </div>
          )}

          {/* Reply Suggestions */}
          {showReplies && analysis && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Reply Suggestions</h2>
                <p className="text-base text-gray-600">Choose what feels natural to you - these are just ideas</p>
              </div>

              <div className="space-y-4">
                {analysis.suggestions.map((suggestion, index) => {
                  const colorClasses = getCategoryColor(suggestion.category);
                  const icon = getCategoryIcon(suggestion.category);
                  const label = getCategoryLabel(suggestion.category);
                  
                  return (
                    <div key={index} className="bg-white rounded-2xl shadow-sm p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <div className={`w-8 h-8 ${colorClasses.bg} rounded-lg flex items-center justify-center`}>
                            <i className={`${icon} text-base ${colorClasses.text}`}></i>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">{label}</span>
                        </div>
                        <button 
                          onClick={() => navigator.clipboard.writeText(suggestion.text)}
                          className="text-gray-400 hover:text-blue-600 transition-colors cursor-pointer"
                          title="Copy to clipboard"
                        >
                          <i className="ri-file-copy-line text-lg"></i>
                        </button>
                      </div>
                      <p className="text-sm text-gray-700 bg-gray-50 rounded-xl p-4 mb-3">
                        {suggestion.text}
                      </p>
                      <p className="text-xs text-gray-600">
                        <strong>Why this works:</strong> {suggestion.reason}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
                <button
                  onClick={() => {
                    setMessage('');
                    setAnalysis(null);
                    setShowReplies(false);
                    setError(null);
                  }}
                  className="px-6 py-3 bg-gray-100 text-gray-900 text-sm font-semibold rounded-full hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Analyze Another Message
                </button>
                <Link
                  to="/learning"
                  className="px-6 py-3 bg-blue-600 text-white text-sm font-semibold rounded-full hover:bg-blue-700 transition-colors text-center whitespace-nowrap cursor-pointer"
                >
                  Learn More About Communication
                </Link>
              </div>
            </div>
          )}

          {/* Help Section */}
          {!analysis && (
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 md:p-8 mt-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">Need Help Right Now?</h3>
                  <p className="text-sm text-gray-700 mb-4">
                    If you're in an unsafe situation or feeling threatened, please reach out to a trusted adult, school counselor, or contact a crisis helpline immediately.
                  </p>
                  <Link to="/disclaimer" className="text-sm text-green-700 font-medium hover:text-green-800 whitespace-nowrap cursor-pointer">
                    View Crisis Resources <i className="ri-arrow-right-line ml-1"></i>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
